import React, { useState } from "react";
import type { OptionType, Policy } from "../../@lib/types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";

import { useEffect, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useLazyQuery, gql } from "@apollo/client";
import { useNotify } from "../../components/@common/NotificationsProvider";
import { Autocomplete, Button, TextField } from "@mui/material";
import { toastTypesKeys } from "../../utils/constants";
import { toastHandler } from "../../utils/helpers";
import {
  ConditionalRenderer,
  useLanguageTranslator,
  StatusTag,
} from "../../components";
import { ToastContainer } from "react-toastify";
import { ModalCloseIcon } from "../../@lib/icons";

const policyQueryDocument = gql`
  query PolicyQuery($policyId: ID!) {
    policy(id: $policyId) {
      status
      statusLabel
      description
      policyCategory {
        name
      }
      theme {
        title
      }
      country {
        name
      }
      geographicalArea {
        name
      }
    }
  }
`;

export type PoliciesInfoFormProps = {
  disabled?: boolean;
  policyOptions: OptionType[];
  setSelectedListPolicies?: any;
  selectedListPolicies?: any;
  onClickHandler?: any;
};

const style = {
  position: "absolute",
  margin: "2% 0px",
  width: "98%",
  left: "1%",
  bgcolor: "background.paper",
  borderRadius: "10px",
  boxShadow: 24,
  overflow: "auto",
  height: "90dvh",
};

const PoliciesInfoForm: React.FC<PoliciesInfoFormProps> = (props) => {
  const {
    policyOptions,
    disabled,
    setSelectedListPolicies,
    selectedListPolicies,
    onClickHandler,
  } = props;

  const [selectedValue, setSelectedValue] = useState<any>(null);

  const notify = useNotify();
  const { trigger, getValues } = useFormContext();
  const { translator } = useLanguageTranslator();

  const [getPolicy, { data, loading }] = useLazyQuery<{
    policy: Pick<
      Policy,
      | "status"
      | "statusLabel"
      | "description"
      | "policyCategory"
      | "theme"
      | "country"
      | "geographicalArea"
    >;
  }>(policyQueryDocument);

  /**
   * loadPolicy()
   */
  const loadPolicy = useCallback(async () => {
    try {
      const policyId = getValues("policyId");

      if (policyId)
        await getPolicy({
          variables: {
            policyId,
          },
        });
    } catch (err) {
      notify.error(err as Error);
    }
  }, [getPolicy, getValues, notify]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const handleValueChange = (event: any, newValue: any) => {
    setSelectedValue(newValue);

    if (newValue === null) return;

    const isDuplicate = selectedListPolicies.some(
      (item: any) => item?.value === newValue?.value
    );

    if (isDuplicate) {
      toastHandler("Already Selected", toastTypesKeys.error);
      return;
    }

    setSelectedListPolicies([...selectedListPolicies, newValue]);
  };

  function handleRemoveClick(value: string) {
    const modifiedSelectedListPolicies = selectedListPolicies?.filter(
      (item: any) => item.value !== value
    );
    setSelectedListPolicies(modifiedSelectedListPolicies);
  }

  return (
    <Stack gap={2}>
      <ToastContainer />

      <Stack direction="row" gap={2} alignItems="flex-end">
        {/* <Controller
          name="policyId"
          rules={{ required: "Required!" }}
          render={({ field, fieldState: { error } }) => (
            <FormControl
              error={!!error}
              disabled={disabled || loading}
              sx={{ flexBasis: { xs: "100%", md: "60%", lg: "40%" } }}
            ></FormControl>
          )}
        /> */}
      </Stack>

      {data && !loading && (
        <Stack direction="row" gap={1}>
          <StatusTag
            label={data.policy.statusLabel}
            status={data.policy.status}
          />
          <Chip label={data.policy.policyCategory.name} />
          <Chip label={data.policy.theme.title} />
          <Chip label={data.policy.country.name} />
          <Chip label={data.policy.geographicalArea.name} />
        </Stack>
      )}
      <Box
        component={Paper}
        variant="outlined"
        bgcolor="grey.100"
        sx={{ minHeight: 300 ,maxHeight: 300, p: 2, overflowY:"auto" }}
      >
        {loading && <CircularProgress />}
        <ConditionalRenderer condition={selectedListPolicies?.length <= 0}>
          <Typography variant="caption">
            {translator("No policies already selected")}
          </Typography>
        </ConditionalRenderer>
        <ConditionalRenderer condition={selectedListPolicies?.length > 0}>
          {selectedListPolicies?.map((elem: any) => {
            return (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  variant="caption"
                  fontWeight={"700"}
                  sx={{
                    display: "flex",
                    color: "#00AAB0",
                    marginBottom: "4px",
                    background: "#eff1f7",
                    border: "1px solid #00AAB0",
                    borderRadius: "30px",
                    p: "4px",
                    alignItems: "center",
                  }}
                >
                  {elem?.label}
                </Typography>
                <Button onClick={() => handleRemoveClick(elem?.value)}>
                  <ModalCloseIcon />
                </Button>
              </div>
            );
          })}
        </ConditionalRenderer>
        {data && !loading && <Typography>{data.policy.description}</Typography>}
      </Box>
      <Stack direction={{ md: "row" }} gap={2} alignItems={{ md: "flex-end" }}>
        <Autocomplete
          disablePortal
          options={policyOptions || []}
          sx={{ width: 300 }}
          value={selectedValue}
          onChange={handleValueChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`${translator("support.Select")} ${translator(
                "Policies"
              )}`}
            />
          )}
        />
        {/* <Button variant="contained" onClick={onClickHandler}>
          Create new policy
        </Button> */}
      </Stack>
    </Stack>
  );
};

export default PoliciesInfoForm;
